// frontend/src/utils/dcaSimulator.js
//
// Dollar-cost-averaging comparison: invest a fixed amount into the stock on the
// first session of every month (fractional shares, no fees — deliberately
// simple) vs the same monthly amount into a term deposit compounding monthly.
// Uses dividend-adjusted closes, so the stock side implicitly reinvests
// dividends — the fair comparison against interest compounding.

const candleDate = (candle) => {
  const ts = candle.timestamp || candle.time;
  if (typeof ts === 'number') {
    return new Date(ts > 1_000_000_000_000 ? ts : ts * 1000);
  }
  return new Date(ts);
};

/**
 * @param {Array} dailyCandles
 * @param {number} monthlyAmount - contribution per month (e.g., 10,000,000 VND)
 * @param {number} depositRatePctYear - term deposit annual rate, e.g. 8
 * @returns {{series: Array, summary: Object}|null}
 */
export function simulateDCA(dailyCandles, monthlyAmount, depositRatePctYear) {
  if (!Array.isArray(dailyCandles) || dailyCandles.length < 40 || !(monthlyAmount > 0)) {
    return null;
  }

  const monthlyRate = depositRatePctYear / 100 / 12;
  let shares = 0;
  let invested = 0;
  let depositValue = 0;
  let months = 0;
  let prevMonthKey = null;

  const series = [];
  for (let i = 0; i < dailyCandles.length; i++) {
    const candle = dailyCandles[i];
    const close = Number(candle.close);
    if (!Number.isFinite(close) || close <= 0) continue;

    const date = candleDate(candle);
    if (Number.isNaN(date.getTime())) continue;
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

    if (monthKey !== prevMonthKey) {
      // first session of a new month: interest accrues, then both sides
      // receive the fresh contribution
      depositValue = depositValue * (1 + monthlyRate) + monthlyAmount;
      shares += monthlyAmount / close;
      invested += monthlyAmount;
      months += 1;
      prevMonthKey = monthKey;
    }

    series.push({
      time: date.toISOString().slice(0, 10),
      stock: shares * close,
      deposit: depositValue,
      invested,
    });
  }

  if (months < 13) return null; // need at least a year to say anything

  // downsample for charting
  const step = Math.max(1, Math.floor(series.length / 200));
  const sampled = series.filter((_, i) => i % step === 0 || i === series.length - 1);

  const last = series[series.length - 1];
  const years = months / 12;
  const annualized = (value) =>
    invested > 0 && value > 0 ? (Math.pow(value / invested, 1 / years) - 1) * 100 : null;

  return {
    series: sampled,
    summary: {
      months,
      invested,
      stockValue: last.stock,
      stockProfit: last.stock - invested,
      stockReturnPct: (last.stock / invested - 1) * 100,
      stockAnnualizedPct: annualized(last.stock),
      depositValue: last.deposit,
      depositProfit: last.deposit - invested,
      depositReturnPct: (last.deposit / invested - 1) * 100,
      depositAnnualizedPct: annualized(last.deposit),
    },
  };
}
